// Lib
import chroma from 'chroma-js';

// Utils
import { asObject, prop } from '../utils/immutableHelper';
import { hexToRgb, inferColorSpace, parseHslText, parseRgbText, rgbToHex } from './colorSpaceUtil';

// Constants
import { BACKGROUND_COLORS, COLOR_SPACE, COLORS, SKETCH_BACKGROUND_COLORS } from './colorConstants';

export const getColorObjectSpace = (colorObject) => prop('space', colorObject);

const getHexColorObject = (hexCode) => {
    const rgb = hexToRgb(hexCode);

    if (!rgb) return null;

    const [r, g, b] = rgb;
    const fullHexCode = rgbToHex([r, g, b]);

    return {
        space: COLOR_SPACE.HEX,
        value: fullHexCode,
        r,
        g,
        b,
    };
};

const getRgbColorObject = ([r, g, b]) => ({
    space: COLOR_SPACE.RGB,
    r,
    g,
    b,
});

const getHslColorObject = ([h, s, l]) => {
    // Set 0 as default value of h, since chroma-js is able to return NaN hue values
    // when color is black or white
    const hRounded = Math.round(h || 0);
    const sRounded = Math.round(s);
    const lRounded = Math.round(l);

    return {
        space: COLOR_SPACE.HSL,
        h: hRounded,
        s: sRounded,
        l: lRounded,
    };
};

const getChromaColor = (color) => {
    const colorObj = asObject(color);

    switch (colorObj?.space) {
        case COLOR_SPACE.HEX:
            return chroma(colorObj.value);
        case COLOR_SPACE.RGB:
            return chroma([colorObj.r, colorObj.g, colorObj.b]);
        case COLOR_SPACE.HSL:
            return chroma({ h: colorObj.h, s: colorObj.s / 100, l: colorObj.l / 100 });
        default:
            return null;
    }
};

export const getColorObjectFromText = (text) => {
    if (!text) return null;

    const colorMapEntry = COLORS[text] || BACKGROUND_COLORS[text] || SKETCH_BACKGROUND_COLORS[text];
    if (colorMapEntry) return getHexColorObject(colorMapEntry.hex);

    const colorSpace = inferColorSpace(text);

    switch (colorSpace) {
        case COLOR_SPACE.HEX:
            return getHexColorObject(text);
        case COLOR_SPACE.RGB: {
            const rgb = parseRgbText(text);
            return getRgbColorObject(rgb);
        }
        case COLOR_SPACE.HSL: {
            const hsl = parseHslText(text);
            return getHslColorObject(hsl);
        }
        default:
            return null;
    }
};

export const convertToColorSpace = (colorObject, newColorSpace) => {
    const prevColorSpace = getColorObjectSpace(colorObject);

    if (prevColorSpace === newColorSpace) return colorObject;

    const chromaColor = getChromaColor(colorObject);
    if (!chromaColor) return null;

    switch (newColorSpace) {
        case COLOR_SPACE.HEX: {
            const hex = chromaColor.hex();
            return getHexColorObject(hex);
        }
        case COLOR_SPACE.RGB: {
            const rgb = chromaColor.rgb();
            return getRgbColorObject(rgb);
        }
        case COLOR_SPACE.HSL: {
            const [h, s, l] = chromaColor.hsl();
            return getHslColorObject([h, s * 100, l * 100]);
        }
        default:
            return null;
    }
};

/**
 * Take in either a color object or string (predefined color or valid color value), and returns
 * a color object in the format of `newColorSpace` provided.
 *
 * If `newColorSpace` not given, use inferred color space.
 */
export const parseColorObject = (color, newColorSpace) => {
    // if not a color object, convert it to one first
    const colorObject = getColorObjectSpace(color) ? color : getColorObjectFromText(color);

    if (!newColorSpace) return colorObject;

    return convertToColorSpace(colorObject, newColorSpace);
};

export const getColorObjectDisplayValue = (color) => {
    const colorSpace = getColorObjectSpace(color);
    switch (colorSpace) {
        case COLOR_SPACE.HEX:
            return prop('value', color);
        case COLOR_SPACE.RGB:
            return `${prop('r', color)},${prop('g', color)},${prop('b', color)}`;
        case COLOR_SPACE.HSL:
            return `${prop('h', color)},${prop('s', color)}%,${prop('l', color)}%`;
        default:
            return null;
    }
};

export const getColorObjectCssValue = (color) => {
    const colorSpace = getColorObjectSpace(color);
    switch (colorSpace) {
        case COLOR_SPACE.HEX:
            return prop('value', color);
        case COLOR_SPACE.RGB:
            return `rgb(${prop('r', color)},${prop('g', color)},${prop('b', color)})`;
        case COLOR_SPACE.HSL:
            return `hsl(${prop('h', color)},${prop('s', color)}%,${prop('l', color)}%)`;
        default:
            return null;
    }
};
